import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useAuthStore } from '@/stores/authStore';

import { useLockSession, useLogin, useLogout, useUnlockSession } from '../auth/useAuth';

interface UnlockFields {
  password: string;
}

const ACTIVITY_EVENTS: ReadonlyArray<keyof DocumentEventMap> = [
  'mousemove',
  'keydown',
  'click',
  'wheel',
  'touchstart',
];

interface InactivityLockProps {
  /** Idle timeout in milliseconds before the screen auto-locks. */
  idle_ms: number;
}

/**
 * Auto-locks the session after `idle_ms` of inactivity. The overlay only requires the
 * current user's password to dismiss — no full re-auth — so doctor drafts and shift
 * state remain in memory (rule §28).
 */
export function InactivityLock({ idle_ms }: InactivityLockProps): JSX.Element | null {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const is_locked = useAuthStore((s) => s.is_locked);
  const lockSession = useLockSession();
  const unlockSession = useUnlockSession();
  const { submitLogin } = useLogin();
  const logout = useLogout();
  const timer_ref = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<UnlockFields>({ defaultValues: { password: '' } });

  // Reset the idle timer on activity. The effect only runs while we have a logged-in
  // user, so signed-out routes (login, setup) don't accidentally lock themselves.
  useEffect(() => {
    if (!user || is_locked) return;

    const resetTimer = (): void => {
      if (timer_ref.current !== null) window.clearTimeout(timer_ref.current);
      timer_ref.current = window.setTimeout(() => {
        void lockSession();
      }, idle_ms);
    };

    resetTimer();
    for (const ev of ACTIVITY_EVENTS) {
      document.addEventListener(ev, resetTimer, { passive: true });
    }
    return () => {
      if (timer_ref.current !== null) window.clearTimeout(timer_ref.current);
      for (const ev of ACTIVITY_EVENTS) document.removeEventListener(ev, resetTimer);
    };
  }, [user, is_locked, idle_ms, lockSession]);

  if (!user || !is_locked) return null;

  const onSubmitUnlock = async (values: UnlockFields): Promise<void> => {
    setError(null);
    try {
      await submitLogin(user.email, values.password);
      await unlockSession();
      form.reset();
    } catch {
      setError(t('auth.invalid_credentials'));
    }
  };

  const onClickLogoutFromLock = async (): Promise<void> => {
    await logout();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/90 backdrop-blur"
    >
      <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8 shadow-lg">
        <h2 className="text-lg font-semibold text-card-foreground">{t('auth.session_locked')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('auth.session_locked_subtitle', { name: user.full_name })}
        </p>
        <form onSubmit={form.handleSubmit(onSubmitUnlock)} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="unlock-password">{t('auth.password_label')}</Label>
            <Input
              id="unlock-password"
              type="password"
              autoFocus
              autoComplete="current-password"
              {...form.register('password', { required: true })}
            />
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </div>
          <div className="flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                void onClickLogoutFromLock();
              }}
            >
              {t('auth.sign_out')}
            </Button>
            <Button type="submit">{t('auth.unlock')}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
